import React from "react";
import { useFormContext, useFieldArray } from "react-hook-form";

/**
 * Pricing rules sub-form.
 *
 * `useFieldArray` is always called so the hook order is stable.
 */
export default function PricingRulesEditor({ path, type, basis, errors }) {
  const { register, control, formState: { errors: formErrors }, trigger } = useFormContext();
  const get = (obj, p) => p.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);

  // Always initialise the array hook (even if not used)
  const { fields, append, remove } = useFieldArray({
    control,
    name: path, // path points to rules[]
  });

  /* ── Flat rate ─────────────────────────────────────────────── */
  if (type === "flat") {
    return (
      <div>
        <label className="block text-xs font-medium">Flat Rate ($) *</label>
         <input type="number" step="0.01" min="0"
           {...register(`${path}.rate`, { valueAsNumber: true })}
         />
       {errors?.rate && (
         <p className="mt-1 text-xs text-red-600">{errors.rate.message}</p>
       )}
      </div>
    );
  }

  /* ── Dynamic rate (weight OR dimension) ────────────────────── */
  if (type === "dynamic") {
    return (
      <div className="grid gap-3 sm:grid-cols-3">
        {[
          { label: "Base Rate ($)", key: "baseRate" },
          { label: basis === "weight" ? "Rate per kg ($)" : "Rate per m³ ($)", key: "ratePerKg" },
          { label: "Minimum Charge ($)", key: "minCharge" },
        ].map(({ label, key }) => (
          <div key={key}>
            <label className="block text-xs font-medium">{label}</label>
             <input
               type="number" step="0.01" min="0"
               {...register(`${path}.${key}`, { valueAsNumber: true })}
               className="mt-1 w-full rounded border p-2"
             />
            {errors?.[key] && (
             <p className="mt-1 text-xs text-red-600">{errors[key].message}</p>
           )}
          </div>
        ))}
      </div>
    );
  }

  /* ── Tiered table (weight only - simplest UX) ──────────────── */
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium">Tiered Pricing Rules *</span>
        <button
          type="button"
          onClick={() => { append({ maxWeight: undefined, rate: undefined }); trigger(path); }}
          className="rounded bg-indigo-600 px-2 py-0.5 text-[10px] text-white hover:bg-indigo-700"
        >
          + Add Tier
        </button>
      </div>

      {fields.length === 0 && (
        <p className="text-[10px] text-gray-500">No tiers</p>
      )}

      {fields.map((row, idx) => (
        <div
          key={row.id}
          className="mb-1 flex items-center gap-2 text-xs last:mb-0"
        >
          <input
            type="number"
            step="0.01"
            min="0"
            {...register(`${path}.${idx}.maxWeight`, {
              valueAsNumber: true,
              onChange: () => trigger(path),   // re-run array-level tests (duplicates, min length, etc.)
            })}
            placeholder="Max Weight (kg)"
            className="w-1/2 rounded border p-1"
          />
           {errors?.[idx]?.maxWeight && (
             <p className="text-[10px] text-red-600">{errors[idx].maxWeight.message}</p>
           )}
          <input
            type="number"
            step="0.01"
            min="0"
            {...register(`${path}.${idx}.rate`, {
              valueAsNumber: true,
              onChange: () => trigger(path),
            })}
            placeholder="Rate ($)"
            className="w-1/2 rounded border p-1"
          />
           {errors?.[idx]?.rate && (
             <p className="text-[10px] text-red-600">{errors[idx].rate.message}</p>
           )}
          <button
            type="button"
            onClick={() => { remove(idx); trigger(path); }}
            className="text-red-500 hover:underline"
          >
            ✕
          </button>
        </div>
      ))}
      {/* array-level errors (“Add at least one tier”, “Duplicate max-weight”, etc.) */}
      {(() => {
        const rootMsg = errors?.root?.message || errors?.message;
        return rootMsg ? <p className="mt-1 text-xs text-red-600">{rootMsg}</p> : null;
      })()}
    </div>
  );
}
